import { Dashboard } from '~/javascripts/dashboard';

class IntegrationsByMonth {
  constructor() {
    this.data = null;
    this.currentMonthBar = null;
    this.selectedMonthEl = null;
    this.selectedMonthElIndex = null;
    this.selectedMonthTitleEl = null;
    this.selectedMonthChart = null;
    this.newIntegrationsCountEl = null;
    this._integrationChart = null;

    document.addEventListener('turbo:before-cache', () => {
      if (this._integrationChart) {
        this._integrationChart.destroy();
        this._integrationChart = null;
      }
      if (this.selectedMonthChart) {
        this.selectedMonthChart.destroy();
        this.selectedMonthChart = null;
      }
      this.selectedMonthEl = null;
      this.selectedMonthElIndex = null;
      this.selectedMonthTitleEl = null;
      this.newIntegrationsCountEl = null;
      this.currentMonthBar = null;
    });
  }

  static create() {
    const instance = new IntegrationsByMonth();

    return {
      setData: instance.setData.bind(instance),
      renderChart: instance.renderChart.bind(instance),
    };
  }

  setData(value) {
    this.data = value;
  }

  get integrationChart() {
    const canvas = document.getElementById('integrations-chart');
    if (!canvas) return null;

    if (this._integrationChart && this._integrationChart.canvas !== canvas) {
      this._integrationChart.destroy();
      this._integrationChart = null;
    }

    if (this._integrationChart) return this._integrationChart;

    const ctx = canvas.getContext('2d');
    const labels = [];
    const values = [];

    this.data.forEach((item) => {
      const value = item[1].reduce((acc, currentValue) => acc + currentValue[1], 0);
      labels.push(item[0]);
      values.push(value);
    });

    this._integrationChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Number of Integrations by Month',
            data: values,
            backgroundColor: [
              'rgba(0, 104, 229, 1)',
              'rgba(10, 217, 225, 1)',
              'rgba(121, 20, 239, 1)',
              'rgba(219, 105, 255, 1)',
              'rgba(254, 144, 48, 1)',
              'rgba(255, 224, 46, 1)',
              'rgba(239, 10, 54, 1)',
              'rgba(255, 151, 138, 1)',
              'rgba(28, 156, 126, 1)',
              'rgba(16, 86, 70, 1)',
            ],
            borderColor: [
              'rgba(0, 104, 229, 1)',
              'rgba(10, 217, 225, 1)',
              'rgba(121, 20, 239, 1)',
              'rgba(219, 105, 255, 1)',
              'rgba(254, 144, 48, 1)',
              'rgba(255, 224, 46, 1)',
              'rgba(239, 10, 54, 1)',
              'rgba(255, 151, 138, 1)',
              'rgba(28, 156, 126, 1)',
              'rgba(16, 86, 70, 1)',
            ],
            borderWidth: 1,
          },
        ],
      },
      options: {
        plugins: {
          legend: { display: false },
        },
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          y: {
            display: true,
            ticks: {
              beginAtZero: true,
              stepSize: 10,
            },
          },
        },
        onClick: (event) => this.handleChartClick(event),
      },
    });

    return this._integrationChart;
  }

  handleChartClick(event) {
    const selectedMonthBar = this.integrationChart.getElementsAtEventForMode(
      event,
      'nearest',
      { intersect: true },
      false
    )[0];

    if (!selectedMonthBar || selectedMonthBar.index === this.selectedMonthElIndex) {
      this.selectedMonthEl.classList.remove('active');
      this.selectedMonthElIndex = null;
      return;
    }

    this.currentMonthBar = selectedMonthBar;
    this.selectedMonthElIndex = selectedMonthBar.index;

    const title = this.integrationChart.data.labels[selectedMonthBar.index];
    const selectedMonthData = this.data.find((item) => item[0] === title)[1];

    const labels = selectedMonthData.map((item) => `${item[0]} - ${item[1]}`);
    const values = selectedMonthData.map((item) => item[1]);

    if (this.selectedMonthChart) {
      this.selectedMonthChart.data.labels = labels;
      this.selectedMonthChart.data.datasets.forEach((dataset) => {
        dataset.data = values;
      });
      this.selectedMonthChart.update();
    } else {
      this.selectedMonthChart = this.createChartForColumn(labels, values);
    }

    const totalCount = values.reduce((acc, val) => acc + val, 0);
    this.selectedMonthEl.classList.add('active');
    this.selectedMonthTitleEl.textContent = title;
    this.newIntegrationsCountEl.textContent = ` ${totalCount}`;

    const downloadBtn = document.querySelector('#download-properties-list-button');
    downloadBtn.href = `${downloadBtn.dataset.path}?month=${title}`;
    this.selectedMonthEl.scrollIntoView({ behavior: 'smooth' });
  }

  createChartForColumn(labels, values) {
    const ctx = document.getElementById('month-distribution-chart').getContext('2d');

    return new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [
          {
            label: 'Distribution by Integration Type',
            data: values,
            backgroundColor: [
              'rgba(0, 104, 229, 1)',
              'rgba(10, 217, 225, 1)',
              'rgba(121, 20, 239, 1)',
              'rgba(219, 105, 255, 1)',
              'rgba(254, 144, 48, 1)',
              'rgba(255, 224, 46, 1)',
              'rgba(239, 10, 54, 1)',
              'rgba(28, 156, 126, 1)',
              'rgba(16, 86, 70, 1)',
            ],
            borderColor: [
              'rgba(0, 104, 229, 1)',
              'rgba(10, 217, 225, 1)',
              'rgba(121, 20, 239, 1)',
              'rgba(219, 105, 255, 1)',
              'rgba(254, 144, 48, 1)',
              'rgba(255, 224, 46, 1)',
              'rgba(239, 10, 54, 1)',
              'rgba(28, 156, 126, 1)',
              'rgba(16, 86, 70, 1)',
            ],
            borderWidth: 1,
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          legend: {
            position: 'right',
          },
        },
      },
    });
  }

  renderChart() {
    const integrationsCanvas = document.getElementById('integrations-chart');
    if (!integrationsCanvas) return;

    if (!this.data || this.data.length === 0) {
      console.error('No data provided for integrations chart');
      return;
    }

    this.selectedMonthEl = document.querySelector('#active-month');
    this.selectedMonthTitleEl = document.querySelector('.active-month-title');
    this.newIntegrationsCountEl = document.querySelector('#new-integrations-count');

    if (this.selectedMonthChart) {
      const monthCanvas = document.getElementById('month-distribution-chart');
      if (this.selectedMonthChart.canvas !== monthCanvas) {
        this.selectedMonthChart.destroy();
        this.selectedMonthChart = null;
      }
    }

    this.integrationChart;
  }
}

Dashboard.integrationsByMonth = IntegrationsByMonth.create();
